import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import React, { useEffect } from "react";

import { useWorkspaceStore } from "../../../store/workspace/WorkspaceStore";
import { Market } from "../../../types/workspace";
import { useGetMarketSize } from "../hooks/useGetMarketSize";
import { determineInitialMarket } from "../utils";
import { useGetFilterLabels } from "./hooks/useGetFilterLabels";
import { MarketSelectableCard } from "./MarketSelectableCard";
import { SelectedMarketFilter } from "./SelectedMarketFilters/SelectedMarketFilters";
import CreditProgressBar from "../../../components/Credits/CreditsProgressBar";

interface MarketSizeBySpendProps {
  onlyCards?: boolean;
}

export const MarketSizeBySpend: React.FC<MarketSizeBySpendProps> = ({
  onlyCards = false,
}) => {
  const selectedMarket = useWorkspaceStore((state) => state.selectedMarket);
  const setSelectedMarket = useWorkspaceStore(
    (state) => state.setSelectedMarket
  );

  const isSamDisabled = useWorkspaceStore((state) => state.getIsSamDisabled());
  const isSomDisabled = useWorkspaceStore((state) => state.getIsSomDisabled());
  const setActiveMarket = useWorkspaceStore((state) => state.setSelectedMarket);
  const workspace = useWorkspaceStore((state) => state.draft);

  const onlyCardsStyles = {
    "& .market-selectable-button-card, .market-selectable-card-skeleton, .insufficient-info-card":
      {
        flexBasis: "300px",
        flexGrow: 1,
      },
  };
  const { tam, sam, som } = useGetMarketSize();

  useEffect(() => {
    if (!workspace) return;

    const initialMarket = determineInitialMarket(workspace.configuration);
    if (initialMarket) {
      setActiveMarket(initialMarket);
    }
  }, [workspace, setActiveMarket]);

  const {
    labels,
    spendCategoryData,
    spendCategoryWeights,
    industryData,
    geographyData,
    isLoading,
    productData,
  } = useGetFilterLabels();

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap="16px"
      width="100%"
      minWidth={0}
      maxWidth="100%"
    >
      {!onlyCards && (
        <Typography
          fontSize="20px"
          fontWeight="bold"
          color="#3a3a3a"
          lineHeight={1.2}
        >
          Market Size by Spend
        </Typography>
      )}
      <Box
        display="flex"
        width="100%"
        minWidth={0}
        maxWidth="100%"
        gap="10px"
        sx={onlyCards ? onlyCardsStyles : {}}
        flexWrap="wrap"
      >
        <MarketSelectableCard
          label="TAM"
          selected={selectedMarket === Market.TAM}
          isLoading={tam.loading}
          numberOfCompanies={tam.companyCount}
          onSelect={() => {
            setSelectedMarket(Market.TAM);
          }}
          totalSpend={tam.totalSpend}
        />
        <MarketSelectableCard
          label="SAM"
          selected={selectedMarket === Market.SAM}
          isLoading={sam.loading}
          numberOfCompanies={sam.companyCount}
          onSelect={() => {
            setSelectedMarket(Market.SAM);
          }}
          totalSpend={sam.totalSpend}
          disabled={isSamDisabled}
        />
        <MarketSelectableCard
          label="SOM"
          selected={selectedMarket === Market.SOM}
          isLoading={som.loading}
          numberOfCompanies={som.companyCount}
          onSelect={() => {
            setSelectedMarket(Market.SOM);
          }}
          totalSpend={som.totalSpend}
          disabled={isSomDisabled}
        />
        {!onlyCards && (
          <SelectedMarketFilter
            labels={labels}
            categories={spendCategoryData}
            spendCategoryWeights={spendCategoryWeights}
            industryData={industryData}
            geographyData={geographyData}
            isLoading={isLoading}
            productData={productData}
          />
        )}
      </Box>
      <Box display="flex" justifyContent="flex-end">
        <CreditProgressBar variant="company" />
      </Box>
    </Box>
  );
};
